import { openDocument } from '../helper/documentHelper';
import React, { useRef, useState } from 'react';
import type { JSX } from 'react';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import {
  Button,
  Col,
  Flex,
  Input,
  message,
  Row,
  Skeleton,
  Space,
  Table,
  theme,
  Typography,
} from 'antd';
import type { InternshipProcessWithNames } from '../Types/interfaces';
import { Roles, StepName, StepStatus } from '../Types/interfaces';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  FilterFilled,
  SearchOutlined,
} from '@ant-design/icons';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import FileUploader from './FileUploader';
import type { MenuGroups } from '../Constants/menuKeys';
import { useUserOrganizations } from '../hooks/useOrganizations';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useInternshipProcessesByRole } from '../hooks/useInternshipProcesses';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { DownloadConventionsZip, GetUserById } from '../helper/apiHelper';
import { t } from 'i18next';
import { useCurrentStudyYear } from '../hooks/useStudyYear';
import { StudyYearDropdown } from './StudyYearDropdown';
import { LuDownload } from 'react-icons/lu';
import { format } from 'date-fns';
import { downloadDocuments } from '../Utils/downloadWithUrl';

export type MenuGroupType = (typeof MenuGroups)[keyof typeof MenuGroups];

const EMPTY_INTERNSHIP_PROCESSES_ARRAY: InternshipProcessWithNames[] = [];

interface ConventionSignComponentProps {
  role: number;
  recount?: () => Promise<void>;
  menuGroup: MenuGroupType;
  // shows a study year dropdown and only the conventions of the selected study year
  filterByStudyYear?: boolean;
  // the rows can be selected and the conventions of the selected rows downloaded in a zip file
  enableDownload?: boolean;
}

interface TableFooterProps {
  onFileUpload: () => Promise<void>;
  recount?: () => Promise<void>;
  userId: string;
  role: Roles;
}

interface FilterProps {
  filtered: boolean;
}

enum ProcessSigningStatus {
  Waiting = 1, // waiting for signing
  Signing = 2, // can be signed
  Done = 3, // already signed
}

const TableFooter = ({
  onFileUpload,
  recount,
  userId,
  role,
}: TableFooterProps): React.ReactElement => (
  <>
    <Row justify="end">
      <Col>
        <FileUploader
          role={role}
          onFileUpload={async () => {
            await onFileUpload();
            if (recount) {
              await recount();
            }
          }}
          apiUrl={`${userId}/upload-internship-processes-to-sign`}
          isSignatureDisabled={false}
          alreadySigned={false}
        />
      </Col>
    </Row>
  </>
);

const Filter = ({ filtered }: FilterProps): React.ReactElement => (
  <SearchOutlined
    style={{ color: filtered ? '#1677ff' : undefined, fontSize: '24px' }}
  />
);

const WaitingForSigningComponent = ({
  record,
  role,
}: {
  record: InternshipProcessWithNames;
  role: number;
}) => {
  const userStepName = (() => {
    switch (role) {
      case Roles.OrganizationRepresentative:
        return StepName.signingInternshipConventionByOrganization;

      case Roles.Tutor:
        return StepName.signingInternshipConventionByTutor;

      case Roles.CadruDidactic:
        return StepName.signingInternshipConventionByUniversitySupervisor;

      case Roles.UniversityProjectCoordinator:
        return StepName.SigningInternshipConventionByUniversityProjectCoordinator;

      default:
        return undefined;
    }
  })();

  const isPartOfProcess = record.processSteps.some(
    (step) => step.stepName === userStepName
  );
  const { data: personToSign } = useQuery({
    queryKey: [...QUERY_KEYS.PERSON_TO_SIGN, record],
    queryFn: async () => {
      const steps = record.processSteps;
      const currentStep = steps.find(
        (step) => step.stepStatus === StepStatus.initialized
      );
      if (!currentStep) {
        return null;
      }
      const userToSign = await GetUserById(
        currentStep.responsibleId.toString()
      );
      return userToSign.lastName + ' ' + userToSign.firstName;
    },
  });

  if (!isPartOfProcess) {
    return (
      <Space key={`waiting-${record.id}`}>
        <ExclamationCircleFilled style={{ color: 'gray' }} />
        <span>
          <span>{t('convSignComp.notPartOfProcess')}</span>
        </span>
      </Space>
    );
  } else if (!personToSign || personToSign === 'undefined undefined') {
    return (
      <Space key={`waiting-${record.id}`}>
        <ExclamationCircleFilled style={{ color: 'lightGray' }} />
        <span>
          <span>{t('convSignComp.noConventionDocument')}</span>
        </span>
      </Space>
    );
  } else {
    return (
      <Space key={`waiting-${record.id}`}>
        <ExclamationCircleFilled style={{ color: 'orange' }} />
        {t('convSignComp.waitingForSignWithName', { name: personToSign })}
      </Space>
    );
  }
};

const ConventionSignComponent = (
  props: ConventionSignComponentProps
): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const {
    role,
    recount,
    filterByStudyYear = false,
    enableDownload = false,
  } = props;
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [messageApi, contextHolder] = message.useMessage();
  // the process ids of the selected rows
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);
  const queryClient = useQueryClient();
  const userOrganizationIds = userOrganizations?.map((org) => org.id) ?? [];
  const {
    data: internshipProcesses = EMPTY_INTERNSHIP_PROCESSES_ARRAY,
    isLoading,
  } = useInternshipProcessesByRole(
    loggedInUser.id,
    props.role,
    userOrganizationIds
  );

  // undefined until the user selects a study year, then the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  const { data: currentStudyYear } = useCurrentStudyYear();
  // the dropdown shows the current study year until the user selects another one, the same is filtered here
  const shownStudyYearId = selectedStudyYearId ?? currentStudyYear?.id;

  const shownProcesses = filterByStudyYear
    ? internshipProcesses.filter(
        (process) =>
          process.studyYearId === shownStudyYearId &&
          process.conventionDocumentUrl !== null
      )
    : internshipProcesses;

  type DataIndex = keyof InternshipProcessWithNames;

  const signingStatusOfProcess = (
    process: InternshipProcessWithNames
  ): ProcessSigningStatus => {
    let stepName: string;

    switch (role) {
      case Roles.OrganizationRepresentative:
        stepName = 'SigningInternshipConventionByOrganization';
        break;
      case Roles.Tutor:
        stepName = 'SigningInternshipConventionByTutor';
        break;
      case Roles.CadruDidactic:
        stepName = 'SigningInternshipConventionByUniversitySupervisor';
        break;
      case Roles.Rector:
        stepName = 'SigningInternshipConventionByRector';
        break;
      case Roles.UniversityProjectCoordinator:
        stepName =
          StepName.SigningInternshipConventionByUniversityProjectCoordinator;
        break;
      default:
        stepName = '';
        break;
    }

    const step = process.processSteps.find(
      (step) => step.stepName === stepName
    );
    const stepStatus = step ? step.stepStatus : StepStatus.notStarted;

    if (
      stepStatus === StepStatus.finished ||
      stepStatus === StepStatus.updated
    ) {
      return ProcessSigningStatus.Done;
    }

    if (stepStatus === StepStatus.initialized) {
      return ProcessSigningStatus.Signing;
    }

    return ProcessSigningStatus.Waiting;
  };

  // part for searching for names
  function handleSearch(
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  }

  const updateSigningStatus = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_ROLE,
    });
    internshipProcesses.forEach((process) => {
      signingStatusOfProcess(process);
    });
    if (recount) {
      await recount();
    }
  };

  const handleReset = (clearFilters: () => void): void => {
    clearFilters();
    setSearchText('');
  };

  // Extracted handlers to avoid deep nesting
  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    e.stopPropagation();
  };

  const GetStepNameFromRole = (role: Roles): string => {
    switch (role) {
      case Roles.OrganizationRepresentative:
        return 'SigningInternshipConventionByOrganization';
      case Roles.Tutor:
        return 'SigningInternshipConventionByTutor';
      case Roles.CadruDidactic:
        return 'SigningInternshipConventionByUniversitySupervisor';
      case Roles.Rector:
        return 'SigningInternshipConventionByRector';
      case Roles.UniversityProjectCoordinator:
        return StepName.SigningInternshipConventionByUniversityProjectCoordinator;
      default:
        return '';
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setSelectedKeys: (selectedKeys: React.Key[]) => void
  ): void => {
    setSelectedKeys(e.target.value ? [e.target.value] : []);
  };

  const handleInputPressEnter = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    handleSearch(selectedKeys, confirm, dataIndex);
  };

  const handleSearchClick = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    handleSearch(selectedKeys, confirm, dataIndex);
  };

  const handleResetClick = (clearFilters: (() => void) | undefined): void => {
    if (clearFilters) handleReset(clearFilters);
  };

  const handleFilterClick = (
    confirm: FilterDropdownProps['confirm'],
    selectedKeys: string[],
    dataIndex: DataIndex
  ): void => {
    confirm({ closeDropdown: false });
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleCloseClick = (close: () => void): void => {
    close();
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<InternshipProcessWithNames> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <button
        type="button"
        onKeyDown={handleInputKeyDown}
        style={{ border: 'none' }}
      >
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={t('convSignComp.searchStudent')}
            value={selectedKeys[0]}
            onChange={(e) => handleInputChange(e, setSelectedKeys)}
            onPressEnter={() =>
              handleInputPressEnter(
                selectedKeys as string[],
                confirm,
                dataIndex
              )
            }
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleSearchClick(selectedKeys as string[], confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t('convSignComp.search')}
            </Button>
            <Button
              onClick={() => handleResetClick(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              {t('convSignComp.reset')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() =>
                handleFilterClick(confirm, selectedKeys as string[], dataIndex)
              }
            >
              {t('convSignComp.filter')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleCloseClick(close)}
            >
              {t('convSignComp.close')}
            </Button>
          </Space>
        </div>
      </button>
    ),
    filterIcon: (filtered: boolean) => <Filter filtered={filtered} />,
    onFilter: (value, record) =>
      JSON.stringify(record[dataIndex])
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible: boolean): void => {
      if (visible) {
        setTimeout(() => {
          if (searchInput.current) {
            searchInput.current.select();
          }
        }, 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleDownload = async (
    url: string,
    processId: number
  ): Promise<void> => {
    const status = internshipProcesses.find(
      (process) => process.id === processId
    )?.currentUniversityStepName;
    await openDocument(url, status);
  };
  const filtersForStudyLine = Array.from(
    new Set(shownProcesses.map((p) => p.studyLineName))
  )
    .filter((studyLine) => studyLine) // check and remove falsy values
    .map((studyLine) => ({
      text: studyLine,
      value: studyLine,
    }));

  const filtersForOrganization = Array.from(
    new Set(shownProcesses.map((p) => p.organizationName))
  )
    .filter((orgName) => orgName)
    .map((orgName) => ({
      text: orgName,
      value: orgName,
    }));

  const filtersForSession = Array.from(
    new Set(shownProcesses.map((p) => p.sessionName))
  )
    .filter((sessionName) => sessionName)
    .map((sessionName) => ({
      text: sessionName,
      value: sessionName,
    }));

  const columns: TableColumnsType<InternshipProcessWithNames> = [
    {
      title: t('convSignComp.student'),
      dataIndex: 'studentName',
      key: 'studentName',
      ...getColumnSearchProps('studentName'),
    },
    {
      title: t('convSignComp.org'),
      dataIndex: 'organizationName',
      key: 'organizationName',
      filters: filtersForOrganization,
      onFilter: (value, record) => record.organizationName === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('convSignComp.studyline'),
      dataIndex: 'studyLineName',
      key: 'studyline',
      filters: filtersForStudyLine,
      onFilter: (value, record) => record.studyLineName === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('convSignComp.session'),
      dataIndex: 'sessionName',
      key: 'session',
      filters: filtersForSession,
      onFilter: (value, record) => record.sessionName === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('convSignComp.conv'),
      dataIndex: 'conventionDocumentUrl',
      key: 'convention',
      render: (text: string, process: InternshipProcessWithNames) => (
        <Typography.Link
          onClick={() => handleDownload(text, process.id)}
          style={{ marginTop: 5 }}
          disabled={text == null || text === ''}
        >
          {t('convSignComp.viewDoc')}
        </Typography.Link>
      ),
    },
    {
      title: t('convSignComp.sign'),
      key: 'sign',
      filters: [
        { text: t('convSignComp.signed'), value: ProcessSigningStatus.Done },
        {
          text: t('convSignComp.waiting'),
          value: ProcessSigningStatus.Waiting,
        },
        { text: t('convSignComp.sign'), value: ProcessSigningStatus.Signing },
      ],
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      onFilter: (value, record) => signingStatusOfProcess(record) === value,
      filterSearch: true,
      align: 'center',
      width: 200,
      render: (record: InternshipProcessWithNames): React.ReactElement => {
        let content;
        const status = signingStatusOfProcess(record);

        if (status === ProcessSigningStatus.Done) {
          content = (
            <Space key={`signed-${record.id}`}>
              <CheckCircleFilled style={{ color: 'green' }} />
              <span>{t('convSignComp.alreadySigned')}</span>
            </Space>
          );
        } else if (status === ProcessSigningStatus.Signing) {
          content = (
            <Space key={`signed-${record.id}`}>
              <CheckCircleFilled style={{ color: 'blue' }} />
              <span>{t('convSignComp.readyToSign')}</span>
            </Space>
          );
        } else {
          content = <WaitingForSigningComponent record={record} role={role} />;
        }

        return <>{content}</>;
      },
    },
    {
      title: t('convSignComp.uploadDocument'),
      dataIndex: 'uploadAction',
      render: (_, process): JSX.Element => {
        const step = process.processSteps.find(
          (pstep) => pstep.stepName === GetStepNameFromRole(role)
        );
        return (
          <div>
            <FileUploader
              apiUrl={`${loggedInUser.id}/upload-internship-processes-to-sign/${process.id}`}
              documentUrl={process.conventionDocumentUrl}
              documentSigningStatus={process.currentUniversityStepName}
              onFileUpload={async () => {
                await queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_ROLE,
                });
                await queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
                });
                if (recount) {
                  await recount();
                }
              }}
              role={role}
              isSignatureDisabled={
                step?.stepStatus !== StepStatus.initialized &&
                step?.stepStatus !== StepStatus.updated
              }
              alreadySigned={step?.stepStatus === StepStatus.updated}
            />
          </div>
        );
      },
    },
  ];

  const handleDownloadSelected = async (): Promise<void> => {
    try {
      const response = await DownloadConventionsZip(
        selectedRowKeys.map((key) => Number(key))
      );
      const formattedDate = format(new Date(), 'dd-MM-yyyy_HH-mm-ss');
      downloadDocuments(response, `conventii_${formattedDate}`, 'zip');
    } catch {
      messageApi.error(t('legDepComp.downloadFail'));
    }
  };

  if (isLoading) {
    return (
      <div>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <>
      {filterByStudyYear && (
        <Flex
          align="center"
          justify="start"
          gap={8}
          wrap
          style={{ marginBottom: 10 }}
        >
          <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
          <StudyYearDropdown
            value={selectedStudyYearId}
            onChange={setSelectedStudyYearId}
          />
        </Flex>
      )}
      {contextHolder}

      {enableDownload && selectedRowKeys.length > 0 && (
        <Flex
          justify="space-between"
          align="center"
          style={{
            backgroundColor: token.colorFillSecondary,
            paddingLeft: 12,
          }}
        >
          <Typography.Text style={{ color: token.colorTextLabel, margin: 5 }}>
            {t('legDepComp.selectedRows')}: {selectedRowKeys.length}
          </Typography.Text>
          <Button
            type="primary"
            icon={<LuDownload />}
            onClick={handleDownloadSelected}
            style={{ margin: 5, borderRadius: 5 }}
          >
            {t('legDepComp.downloadSelected')}
          </Button>
        </Flex>
      )}

      <Table
        // a new study year resets the table: its column filters and pagination
        key={shownStudyYearId}
        rowSelection={
          enableDownload
            ? {
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
                // a convention which isn't filled in yet has no document to download
                getCheckboxProps: (record: InternshipProcessWithNames) => ({
                  disabled: !record.conventionDocumentUrl,
                }),
              }
            : undefined
        }
        columns={columns}
        scroll={{ x: 'max-content' }}
        dataSource={shownProcesses.map((process) => ({
          ...process,
          key: process.id,
        }))}
        footer={() =>
          TableFooter({
            onFileUpload: updateSigningStatus,
            recount: recount,
            userId: loggedInUser.id.toString(),
            role: role,
          })
        }
        pagination={{
          defaultPageSize: 15,
          position: ['bottomCenter'],
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '15', '30', '50', '100', '200'],
        }}
      ></Table>
    </>
  );
};

export default ConventionSignComponent;
